import { Directive, ElementRef, Input, Optional } from '@angular/core';
import { EventBus } from '@skylab-shared/core';
import { head, unique } from '@skylab-shared/utils';
import { Subject, filter, takeUntil } from 'rxjs';
import { UIEvents } from '../utils/ui-events';
import { VALIDATION_KEY_ATTRIBUTE, ValidationSourceDirective } from './validation-source.directive';

export type ViewValidationViolationItem = {
    errors: string[];
    warnings: string[];
    infos: string[];
    changed?: boolean;
};

export type ViewValidationReadonlyItem = {
    readonly: boolean;
};

@Directive({ selector: '[sklValidate]' })
export class ValidateDirective {
    private _keys: string[] = [];
    private destroy$ = new Subject<void>();
    private validationAreaId!: string | null;

    private readonly violationValidations = new Map<string, ViewValidationViolationItem>();
    private readonly readonlyValidations = new Map<string, ViewValidationReadonlyItem>();

    @Input({alias: 'sklValidate'}) public set keys(val: string | null | undefined | (undefined | null | string)[]) {
        this._keys = !val ? [] : Array.isArray(val) ? val.filter(Boolean) as string[] : [val];
    };

    public constructor(
        private readonly elmRef: ElementRef,
        @Optional()  private readonly validationSourceDirective: ValidationSourceDirective
    ) {}

    public ngOnDestroy(): void {
        this.clearAreaValidation();
        this.destroy$.next();
    }

    public ngAfterViewInit(): void {
        if (!this.validationSourceDirective?.source) return;

        this.validationAreaId = this.getValidationAreaId(this.elmRef.nativeElement);

        this.elmRef.nativeElement.setAttribute(VALIDATION_KEY_ATTRIBUTE, this._keys.join(','));

        this._keys.forEach(elem => {
            const readonlies = this.validationSourceDirective.source.readonlyValidations;
            if (readonlies.has(elem)) {
                const obj = readonlies.get(elem);
                if (obj) {
                    this.setReadonly(elem, obj);
                }
            }
        });

        this._keys.forEach(elem => {
            const viols = this.validationSourceDirective.source.violationsValidations;
            if (viols.has(elem)) {
                const obj = viols.get(elem);
                if (obj) {
                    this.setValidation(elem, obj);
                }
            }
        });

        this.validationSourceDirective.source.readonlyValidation$
            .pipe(
                takeUntil(this.destroy$),
                filter(elem => this._keys.includes(elem.key)))
            .subscribe(elem => {
                if (elem.validation) {
                    this.setReadonly(elem.key, elem.validation);
                }
            });

        this.validationSourceDirective.source.violationValidation$
            .pipe(
                takeUntil(this.destroy$),
                filter(elem => this._keys.includes(elem.key)))
            .subscribe(elem => {
                if (elem.validation) {
                    this.setValidation(elem.key, elem.validation);
                }
            });
    }

    private setValidation(key: string, validation: ViewValidationViolationItem): void {
        if (!validation) return;

        this.violationValidations.set(key, validation);

        const violation: ViewValidationViolationItem = {
            errors: [],
            warnings: [],
            infos: [],
            changed: false
        };

        this.violationValidations.forEach(val => {
            violation.errors = unique([...violation.errors, ...(val.changed ? [...val.errors] : [])]);
            violation.warnings = unique([...violation.warnings , ...val.infos, ...(val.changed ? [...val.warnings] : [])]);
        });

        const elm = this.elmRef.nativeElement;

        const val = {
            ...elm.validation,
            ...violation
        };

        elm.validation = val;
        this.emitToArea(val);
    }

    private setReadonly(key: string, validation: ViewValidationReadonlyItem): void {
        if (!validation) return;

        this.readonlyValidations.set(key, validation);

        let readonly = false;

        this.readonlyValidations.forEach(val => {
            readonly = readonly || val.readonly;
        });

        const elm = this.elmRef.nativeElement;

        const val = {
            ...elm.validation,
            readonly
        };

        elm.validation = val;
    }

    private emitToArea(validation: ViewValidationViolationItem): void {
        const { errors, warnings } = validation;
        EventBus.emit(UIEvents.ControlValidationUpdated, {
            warnings,
            errors,
            areaId: this.validationAreaId,
            key: head(this._keys) ?? '',
        });
    }

    private clearAreaValidation(): void {
        if (this.validationAreaId) {
            EventBus.emit(UIEvents.ControlValidationUpdated, {
                errors: [],
                warnings: [],
                areaId: this.validationAreaId,
                key: head(this._keys) ?? '',
            });
        }
    }

    private getValidationAreaId(element: HTMLElement): string | null {
        const panelElm = element.closest('[validation-area-id]') as HTMLElement | null;
        let id: string | null = null;
        if (panelElm) {
            id = panelElm.getAttribute('validation-area-id');
        }
        return id;
    }
}
